import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  AppBar,
  Toolbar,
  InputAdornment,
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

const Catalogo = () => {
  const [catalogos, setCatalogos] = useState([]);
  const [originalCatalogos, setOriginalCatalogos] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedCatalogo, setSelectedCatalogo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    id_pet: '',
    id_sub_categoria: '',
    id_usuario: '',
    id_marca: '',
    referencia: '',
    descripcion: '',
    codigo_barras: '',
    costo: 0,
    unidades_fraccion: 0,
    descripcion_fraccion: '',
    habilitado: true,
    es_servicio: false,
    es_descontinuado: false,
    es_exento: false,
    es_perm_descuento: false,
  });
  const [subCategorias, setSubCategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userIdPet, setUserIdPet] = useState(null);
  const [userIdUsuario, setUserIdUsuario] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [referenciaLoading, setReferenciaLoading] = useState(false); // Nuevo estado
  const navigate = useNavigate();

  // Decodificar token JWT para obtener id_pet y id_usuario
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        console.log('Payload del token:', payload);
        setUserIdPet(payload.id_pet || null);
        setUserIdUsuario(payload.id_usuario || null);
      } catch (err) {
        console.error('Error al decodificar el token:', err);
        const errorMsg = 'Error al decodificar el token de usuario';
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
      }
    } else {
      const errorMsg = 'No se encontró un token de usuario';
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  }, []);

  // Formatear fechas
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Normalizar datos de catálogo
  const normalizeCatalogo = (catalogo) => {
    if (!catalogo || !catalogo.id_catalogo) {
      console.warn('Catálogo inválido recibido:', catalogo);
      return null;
    }
    return {
      id_catalogo: catalogo.id_catalogo ?? null,
      id_pet: catalogo.id_pet ?? null,
      id_sub_categoria: catalogo.id_sub_categoria ?? null,
      id_usuario: catalogo.id_usuario ?? null,
      id_marca: catalogo.id_marca ?? null,
      referencia: catalogo.referencia ?? '',
      descripcion: catalogo.descripcion ?? '',
      codigo_barras: catalogo.codigo_barras ?? '',
      costo: catalogo.costo ?? 0,
      unidades_fraccion: catalogo.unidades_fraccion ?? 0,
      descripcion_fraccion: catalogo.descripcion_fraccion ?? '',
      habilitado: catalogo.habilitado ?? true,
      es_servicio: catalogo.es_servicio ?? false,
      es_descontinuado: catalogo.es_descontinuado ?? false,
      es_exento: catalogo.es_exento ?? false,
      es_perm_descuento: catalogo.es_perm_descuento ?? false,
      createdAt: catalogo.createdAt ?? null,
      updatedAt: catalogo.updatedAt ?? null,
      tab_categoria: catalogo.tab_categoria ?? '',
    };
  };

  // Obtener catálogos, subcategorías, marcas y pets
  useEffect(() => {
    const fetchData = async () => {
      if (!userIdPet || !userIdUsuario) {
        console.log('Esperando id_pet o id_usuario del usuario...');
        return;
      }
      try {
        setLoading(true);
        const catalogosRes = await api.get(`/catalogos/`);
        console.log('Respuesta de catálogos:', catalogosRes.data);
        const catalogosData = Array.isArray(catalogosRes.data)
          ? catalogosRes.data.filter(item => item !== null && item !== undefined)
          : [];
        const normalizedCatalogos = catalogosData
          .map(normalizeCatalogo)
          .filter(cat => cat !== null && cat.id_catalogo && cat.id_pet === userIdPet);
        console.log('Catálogos normalizados:', normalizedCatalogos);
        setCatalogos(normalizedCatalogos);
        setOriginalCatalogos(normalizedCatalogos);

        const subCategoriasRes = await api.get(`/sub-categorias/`);
        console.log('Respuesta de subcategorías:', subCategoriasRes.data);
        const subCategoriasData = Array.isArray(subCategoriasRes.data)
          ? subCategoriasRes.data
              .filter(item => item !== null && item !== undefined && item.id_pet === userIdPet)
              .map(subCat => ({
                id_sub_categoria: subCat.id_sub_categoria,
                nombre_sub_categoria: subCat.nombre_sub_categoria,
                id_pet: subCat.id_pet,
              }))
          : [];
        console.log('Subcategorías filtradas por id_pet:', subCategoriasData);
        setSubCategorias(subCategoriasData);

        const marcasRes = await api.get('/marcas/');
        console.log('Respuesta de marcas:', marcasRes.data);
        const marcasData = Array.isArray(marcasRes.data)
          ? marcasRes.data.filter(item => item !== null && item !== undefined)
          : [];
        setMarcas(marcasData);

        const petsRes = await api.get('/pets/');
        console.log('Respuesta de pets:', petsRes.data);
        const petsData = Array.isArray(petsRes.data) ? petsRes.data : [];
        setPets(petsData);
        setLoading(false);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        const errorMsg = 'Error al cargar datos: ' + (error.response?.data?.detail || error.message);
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
        setLoading(false);
      }
    };
    fetchData();
  }, [userIdPet, userIdUsuario]);

  // Buscar catálogos en frontend
  useEffect(() => {
    const searchCatalogos = () => {
      console.log('Buscando con término:', searchTerm);
      console.log('Catálogos originales:', originalCatalogos);
      if (!searchTerm) {
        setCatalogos(originalCatalogos);
        console.log('Restaurando catálogos:', originalCatalogos);
        return;
      }
      const filteredCatalogos = originalCatalogos
        .filter(cat => cat && cat.id_catalogo && (cat.referencia || cat.descripcion))
        .filter(cat =>
          (cat.referencia && cat.referencia.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (cat.descripcion && cat.descripcion.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      console.log('Catálogos filtrados:', filteredCatalogos);
      setCatalogos(filteredCatalogos);
      setPage(0);
    };
    searchCatalogos();
  }, [searchTerm, originalCatalogos]);

  // Abrir modal para crear/editar catálogo
  const handleOpenModal = async (catalogo = null) => {
    setSelectedCatalogo(catalogo);
    if (catalogo) {
      setFormData(normalizeCatalogo(catalogo));
    } else {
      setReferenciaLoading(true); // Activa el estado de carga
      setFormData({
        id_pet: userIdPet || '',
        id_sub_categoria: '',
        id_usuario: userIdUsuario || '',
        id_marca: '',
        referencia: '',
        descripcion: '',
        codigo_barras: '',
        costo: 0,
        unidades_fraccion: 0,
        descripcion_fraccion: '',
        habilitado: true,
        es_servicio: false,
        es_descontinuado: false,
        es_exento: false,
        es_perm_descuento: false,
      });
      try {
        const lastReferenciaRes = await api.get('/catalogos/last-referencia');
        setFormData(prevFormData => ({
          ...prevFormData,
          referencia: String(lastReferenciaRes.data.referencia),
        }));
      } catch (err) {
        console.error('Error al obtener la siguiente referencia:', err);
        setFormData(prevFormData => ({
          ...prevFormData,
          referencia: '',
        }));
        const errorMsg = 'Error al obtener la siguiente referencia: ' + (err.response?.data?.detail || err.message);
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
      } finally {
        setReferenciaLoading(false); // Desactiva el estado de carga
      }
    }
    setOpenModal(true);
  };

  // Cerrar Snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  // Guardar catálogo (crear o editar)
  const handleSave = async () => {
    try {
      if (!formData.referencia || !formData.descripcion || !formData.id_pet || !formData.id_sub_categoria || !formData.id_usuario || !formData.id_marca) {
        const errorMsg = 'Referencia, descripción, veterinaria, subcategoría, usuario y marca son obligatorios';
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
        return;
      }
      if (formData.es_servicio || formData.es_descontinuado) {
        formData.costo = 0;
      }
      const payload = {
        id_pet: formData.id_pet,
        id_sub_categoria: formData.id_sub_categoria,
        id_usuario: formData.id_usuario,
        id_marca: formData.id_marca,
        referencia: formData.referencia,
        descripcion: formData.descripcion,
        codigo_barras: formData.codigo_barras || null,
        costo: formData.costo,
        unidades_fraccion: formData.unidades_fraccion,
        descripcion_fraccion: formData.descripcion_fraccion || null,
        habilitado: formData.habilitado,
        es_servicio: formData.es_servicio,
        es_descontinuado: formData.es_descontinuado,
        es_exento: formData.es_exento,
        es_perm_descuento: formData.es_perm_descuento,
      };
      if (selectedCatalogo) {
        const response = await api.put(`/catalogos/${selectedCatalogo.id_catalogo}`, payload);
        const updatedCatalogo = normalizeCatalogo(response.data);
        if (updatedCatalogo) {
          const newCatalogos = catalogos.map(cat =>
            cat.id_catalogo === selectedCatalogo.id_catalogo ? updatedCatalogo : cat
          );
          setCatalogos(newCatalogos);
          setOriginalCatalogos(newCatalogos);
          setSnackbarMessage('Catálogo actualizado exitosamente');
          setSnackbarOpen(true);
        }
      } else {
        const response = await api.post('/catalogos/', payload);
        const newCatalogo = normalizeCatalogo(response.data);
        if (newCatalogo) {
          const newCatalogos = [...catalogos, newCatalogo];
          setCatalogos(newCatalogos);
          setOriginalCatalogos(newCatalogos);
          setSnackbarMessage('Catálogo creado exitosamente');
          setSnackbarOpen(true);
        }
      }
      setOpenModal(false);
      setError(null);
      setPage(0);
    } catch (error) {
      console.error('Error al guardar catálogo:', error);
      const errorMsg = 'Error al guardar catálogo: ' + (error.response?.data?.detail || error.message);
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Deshabilitar catálogo
  const handleToggleHabilitado = async (id, habilitado) => {
    try {
      const response = await api.put(`/catalogos/${id}`, { habilitado: !habilitado });
      const updatedCatalogo = normalizeCatalogo(response.data);
      if (updatedCatalogo) {
        const newCatalogos = catalogos.map(cat =>
          cat.id_catalogo === id ? updatedCatalogo : cat
        );
        setCatalogos(newCatalogos);
        setOriginalCatalogos(newCatalogos);
        setSnackbarMessage(`Catálogo ${updatedCatalogo.habilitado ? 'habilitado' : 'deshabilitado'} exitosamente`);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      const errorMsg = 'Error al actualizar estado: ' + (error.response?.data?.detail || error.message);
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Eliminar catálogo
  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este catálogo?')) return;
    try {
      await api.delete(`/catalogos/${id}`);
      const newCatalogos = catalogos.filter(cat => cat.id_catalogo !== id);
      setCatalogos(newCatalogos);
      setOriginalCatalogos(newCatalogos);
      setSnackbarMessage('Catálogo eliminado exitosamente');
      setSnackbarOpen(true);
      setPage(0);
    } catch (error) {
      console.error('Error al eliminar catálogo:', error);
      const errorMsg = 'Error al eliminar catálogo: ' + (error.response?.data?.detail || error.message);
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Manejo de paginación
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const paginatedCatalogos = catalogos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <AppBar position="relative" sx={{ bgcolor: '#1e293b', mb: 3 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Gestión de Catálogo
          </Typography>
          <TextField
            variant="outlined"
            placeholder="Buscar en catálogo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ bgcolor: 'white', borderRadius: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>
      </AppBar>
      <Container maxWidth={false} disableGutters>
        {loading || !userIdPet || !userIdUsuario ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center">{error}</Typography>
        ) : catalogos.length > 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenModal()}
                >
                  Nuevo Ítem
                </Button>
              </Box>
              <TableContainer
                component={Paper}
                sx={{
                  width: '100%',
                  overflowX: 'auto',
                  boxShadow: 3,
                }}
              >
                <Table
                  sx={{
                    tableLayout: 'auto',
                  }}
                  aria-label="tabla de catálogo"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Referencia
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Descripción
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        SubCategoría
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Marca
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Costo
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Habilitado
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Servicio
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Descontinuado
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Creado
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Actualizado
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Acciones
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedCatalogos.map((row) => {
                      if (!row || !row.id_catalogo) {
                        console.warn('Fila inválida en la tabla:', row);
                        return null;
                      }
                      return (
                        <TableRow
                          key={row.id_catalogo}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {row.id_catalogo}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {row.referencia}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 }, fontWeight: 'bold' }}>
                            {row.descripcion}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {subCategorias.find(sc => sc.id_sub_categoria === row.id_sub_categoria)?.nombre_sub_categoria || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {marcas.find(m => m.id_marca === row.id_marca)?.nombre_marca || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {row.costo.toFixed(4)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            <Switch
                              checked={row.habilitado === true || row.habilitado === false ? row.habilitado : false}
                              onChange={() => handleToggleHabilitado(row.id_catalogo, row.habilitado ?? false)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {row.es_servicio ? 'Sí' : 'No'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {row.es_descontinuado ? 'Sí' : 'No'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {formatDate(row.createdAt)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {formatDate(row.updatedAt)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            <IconButton onClick={() => handleOpenModal(row)} color="primary">
                              <EditIcon />
                            </IconButton>
                            <IconButton onClick={() => handleDelete(row.id_catalogo)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 2 }}>
                <Button
                  variant="contained"
                  disabled={page === 0}
                  onClick={() => handleChangePage(page - 1)}
                >
                  Anterior
                </Button>
                <Typography sx={{ alignSelf: 'center' }}>
                  Página {page + 1} de {Math.ceil(catalogos.length / rowsPerPage)}
                </Typography>
                <Button
                  variant="contained"
                  disabled={page >= Math.ceil(catalogos.length / rowsPerPage) - 1}
                  onClick={() => handleChangePage(page + 1)}
                >
                  Siguiente
                </Button>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6">Sin datos</Typography>
            <Typography color="text.secondary">
              No se encontraron ítems en el catálogo. Crea un nuevo ítem para comenzar.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
              sx={{ mt: 2 }}
            >
              Nuevo Ítem
            </Button>
          </Box>
        )}
        <Dialog open={openModal} onClose={() => setOpenModal(false)}>
          <DialogTitle>{selectedCatalogo ? 'Editar Ítem' : 'Nuevo Ítem'}</DialogTitle>
          <DialogContent>
            <TextField
              label="Referencia"
              fullWidth
              value={formData.referencia}
              onChange={(e) => setFormData({ ...formData, referencia: e.target.value })}
              sx={{ mt: 2 }}
              required
              disabled={!selectedCatalogo || referenciaLoading}
              InputProps={{
                endAdornment: (
                  referenciaLoading && (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  )
                ),
              }}
            />
            <TextField
              label="Descripción"
              fullWidth
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              sx={{ mt: 2 }}
              required
            />
            <TextField
              label="Código de Barras"
              fullWidth
              value={formData.codigo_barras}
              onChange={(e) => setFormData({ ...formData, codigo_barras: e.target.value })}
              sx={{ mt: 2 }}
            />
            <TextField
              select
              label="Veterinaria"
              fullWidth
              value={formData.id_pet}
              onChange={(e) => setFormData({ ...formData, id_pet: e.target.value })}
              sx={{ mt: 2 }}
              required
              disabled
            >
              {pets.length > 0 ? (
                pets.map((pet) => (
                  <MenuItem key={pet.id_pet} value={pet.id_pet}>
                    {pet.nombre_veterinaria}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No hay veterinarias disponibles
                </MenuItem>
              )}
            </TextField>
            <TextField
              select
              label="SubCategoría"
              fullWidth
              value={formData.id_sub_categoria}
              onChange={(e) => setFormData({ ...formData, id_sub_categoria: e.target.value })}
              sx={{ mt: 2 }}
              required
            >
              {subCategorias.length > 0 ? (
                subCategorias.map((subCategoria) => (
                  <MenuItem key={subCategoria.id_sub_categoria} value={subCategoria.id_sub_categoria}>
                    {subCategoria.nombre_sub_categoria}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No hay subcategorías disponibles
                </MenuItem>
              )}
            </TextField>
            <TextField
                    select
                    label="Marca"
                    fullWidth
                    value={formData.id_marca}
                    onChange={(e) => setFormData({ ...formData, id_marca: e.target.value })}
                    sx={{ mt: 2 }}
                    required
                  >
                    {marcas.length > 0 && formData.id_pet ? (
                      marcas
                        .filter(marca => marca.id_pet === formData.id_pet)
                        .map((marca) => (
                          <MenuItem key={marca.id_marca} value={marca.id_marca}>
                            {marca.nombre_marca}
                          </MenuItem>
                        ))
                    ) : (
                      <MenuItem value="" disabled>
                        {formData.id_pet ? 'No hay marcas disponibles para esta veterinaria' : 'Selecciona una veterinaria primero'}
                      </MenuItem>
                    )}
                  </TextField>
            <TextField
              label="Costo"
              type="number"
              fullWidth
              value={formData.costo}
              onChange={(e) => setFormData({ ...formData, costo: parseFloat(e.target.value) || 0 })}
              sx={{ mt: 2 }}
              disabled={formData.es_servicio || formData.es_descontinuado}
            />
            <TextField
              label="Unidades Fracción"
              type="number"
              fullWidth
              value={formData.unidades_fraccion}
              onChange={(e) => setFormData({ ...formData, unidades_fraccion: parseInt(e.target.value) || 0 })}
              sx={{ mt: 2 }}
            />
            <TextField
              label="Descripción Fracción"
              fullWidth
              value={formData.descripcion_fraccion}
              onChange={(e) => setFormData({ ...formData, descripcion_fraccion: e.target.value })}
              sx={{ mt: 2 }}
            />
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Switch
                checked={formData.habilitado}
                onChange={(e) => setFormData({ ...formData, habilitado: e.target.checked })}
                color="primary"
              />
              <Typography>Habilitado</Typography>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Switch
                checked={formData.es_servicio}
                onChange={(e) => setFormData({ ...formData, es_servicio: e.target.checked, costo: e.target.checked ? 0 : formData.costo })}
                color="primary"
              />
              <Typography>Servicio</Typography>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Switch
                checked={formData.es_descontinuado}
                onChange={(e) => setFormData({ ...formData, es_descontinuado: e.target.checked, costo: e.target.checked ? 0 : formData.costo })}
                color="primary"
              />
              <Typography>Descontinuado</Typography>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Switch
                checked={formData.es_exento}
                onChange={(e) => setFormData({ ...formData, es_exento: e.target.checked })}
                color="primary"
              />
              <Typography>Exento</Typography>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Switch
                checked={formData.es_perm_descuento}
                onChange={(e) => setFormData({ ...formData, es_perm_descuento: e.target.checked })}
                color="primary"
              />
              <Typography>Permite Descuento</Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!formData.referencia || !formData.descripcion || !formData.id_pet || !formData.id_sub_categoria || !formData.id_usuario || !formData.id_marca || referenciaLoading}
            >
              Guardar
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ zIndex: 1500 }}
        >
          <Alert onClose={handleSnackbarClose} severity={error ? 'error' : 'success'} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Catalogo;